<template>
    <div class="content-wrapper attendance-page app-listing-page">
        <div class="row align-items-start mb-4">
            <div class="col-lg-6">
                <app-breadcrumb :page-title="$t('attendances')" :icon="'clock'" />
                <!-- <p class="attendance-page__intro text-muted mb-0">
                    {{ $t('attendances_page_intro') }}
                </p> -->
            </div>
            <div class="col-lg-6 mt-3 mt-lg-0">
                <div class="attendance-page__top-actions">
                    <div class="btn-group attendance-page__view-switch" role="group" :aria-label="$t('attendance_view_switch')">
                        <button type="button" class="btn btn-dark" disabled>
                            {{ $t('list_view') }}
                        </button>
                        <a class="btn btn-outline-secondary" href="/attendance/worksite-map">
                            {{ $t('map_view') }}
                        </a>
                    </div>
                    <button
                        type="button"
                        class="btn"
                        :class="filtersOpen ? 'btn-dark' : 'btn-outline-secondary'"
                        @click="toggleFilters"
                    >
                        {{ $t('filters') }}
                        <span v-if="activeFilterCount" class="attendance-page__filter-count">{{ activeFilterCount }}</span>
                    </button>
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        @click="refreshListing"
                    >
                        {{ $t('refresh') }}
                    </button>
                    <button
                        v-if="$can('store_attendances')"
                        type="button"
                        class="btn btn-primary btn-with-shadow"
                        @click="openAddEditModal"
                    >
                        {{ $t('add') }}
                    </button>
                </div>
            </div>
        </div>

        <div class="row mb-2">
            <div class="col-lg-2 col-md-4 col-sm-6 mb-3">
                <div class="attendance-page__stat-card attendance-page__stat-card--present">
                    <span>{{ $t('present') }}</span>
                    <strong>{{ formatCountValue(meta.total_present || 0) }}</strong>
                </div>
            </div>
            <div class="col-lg-2 col-md-4 col-sm-6 mb-3">
                <div class="attendance-page__stat-card attendance-page__stat-card--absent">
                    <span>{{ $t('absent') }}</span>
                    <strong>{{ formatCountValue(meta.total_absent || 0) }}</strong>
                </div>
            </div>
            <div class="col-lg-2 col-md-4 col-sm-6 mb-3">
                <div class="attendance-page__stat-card attendance-page__stat-card--late">
                    <span>{{ $t('late') }}</span>
                    <strong>{{ formatCountValue(meta.total_late || 0) }}</strong>
                </div>
            </div>
            <div class="col-lg-2 col-md-4 col-sm-6 mb-3">
                <div class="attendance-page__stat-card attendance-page__stat-card--overtime">
                    <span>{{ $t('overtime') }}</span>
                    <strong>{{ formatCountValue(meta.total_overtime || 0) }}</strong>
                </div>
            </div>
            <div class="col-lg-2 col-md-4 col-sm-6 mb-3">
                <div class="attendance-page__stat-card attendance-page__stat-card--suspicious">
                    <span>{{ $t('suspicious') }}</span>
                    <strong>{{ formatCountValue(meta.total_suspicious || 0) }}</strong>
                </div>
            </div>
            <div class="col-lg-2 col-md-4 col-sm-6 mb-3">
                <div class="attendance-page__stat-card attendance-page__stat-card--range">
                    <span>{{ $t('date_range') }}</span>
                    <strong>{{ selectedDateLabel }}</strong>
                </div>
            </div>
        </div>

        <transition name="fade">
            <div v-if="filtersOpen" class="card card-with-shadow border-0 mb-4">
                <div class="card-body attendance-page__filters-panel">
                    <div class="attendance-page__filters-head mb-3">
                        <div>
                            <h5 class="mb-1">{{ $t('filters') }}</h5>
                            <p class="text-muted mb-0">{{ $t('attendance_filters_hint') }}</p>
                        </div>
                        <button type="button" class="btn btn-link p-0 text-muted" @click="clearFilters">
                            {{ $t('clear_filters') }}
                        </button>
                    </div>

                    <div class="row">
                        <div class="col-lg-3 col-md-6 mb-3">
                            <label class="attendance-page__label">{{ $t('start_date') }}</label>
                            <input v-model="filters.start_date" type="date" class="form-control" @change="applyFilters" />
                        </div>
                        <div class="col-lg-3 col-md-6 mb-3">
                            <label class="attendance-page__label">{{ $t('end_date') }}</label>
                            <input v-model="filters.end_date" type="date" class="form-control" @change="applyFilters" />
                        </div>
                        <div class="col-lg-3 col-md-6 mb-3">
                            <label class="attendance-page__label">{{ $t('employee') }}</label>
                            <app-input
                                v-model="filters.employee_id"
                                type="search-select"
                                :list="employeeOptions"
                                list-value-field="name"
                                :placeholder="$t('choose_one')"
                                @input="applyFilters"
                            />
                        </div>
                        <div class="col-lg-3 col-md-6 mb-3">
                            <label class="attendance-page__label">{{ $t('location') }}</label>
                            <app-input
                                v-model="filters.location_id"
                                type="search-select"
                                :list="locationOptions"
                                list-value-field="name"
                                :placeholder="$t('choose_one')"
                                @input="applyFilters"
                            />
                        </div>
                        <div class="col-lg-3 col-md-6 mb-3">
                            <label class="attendance-page__label">{{ $t('shift') }}</label>
                            <app-input
                                v-model="filters.shift_id"
                                type="search-select"
                                :list="shiftOptions"
                                list-value-field="name"
                                :placeholder="$t('choose_one')"
                                @input="applyFilters"
                            />
                        </div>
                        <div class="col-lg-3 col-md-6 mb-3">
                            <label class="attendance-page__label">{{ $t('department') }}</label>
                            <app-input
                                v-model="filters.department_id"
                                type="search-select"
                                :list="departmentOptions"
                                list-value-field="name"
                                :placeholder="$t('choose_one')"
                                @input="applyFilters"
                            />
                        </div>
                        <div class="col-lg-3 col-md-6 mb-3">
                            <label class="attendance-page__label">{{ $t('status') }}</label>
                            <select v-model="filters.status" class="form-control" @change="applyFilters">
                                <option value="">{{ $t('all') }}</option>
                                <option v-for="status in statusOptions" :key="status.id" :value="status.id">
                                    {{ status.value }}
                                </option>
                            </select>
                        </div>
                        <div class="col-lg-3 col-md-6 mb-3">
                            <label class="attendance-page__label">{{ $t('items_showing_per_page') }}</label>
                            <select v-model.number="pagination.perPage" class="form-control" @change="changePerPage">
                                <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <div class="card card-with-shadow border-0">
            <div class="card-body attendance-page__table-shell position-relative">
                <app-overlay-loader v-if="isLoading" />

                <div class="attendance-page__table-toolbar">
                    <div>
                        <h5 class="mb-1">{{ $t('attendances') }}</h5>
                        <p class="text-muted mb-0">{{ tableSummary }}</p>
                    </div>

                    <div class="attendance-page__table-toolbar-actions">
                        <div class="attendance-page__search-wrap">
                            <span class="attendance-page__search-icon-wrap" aria-hidden="true">
                                <app-icon name="search" class="attendance-page__search-icon" />
                            </span>
                            <input
                                v-model.trim="searchQuery"
                                type="text"
                                class="form-control attendance-page__search-input"
                                :placeholder="$t('search')"
                                :aria-label="$t('search')"
                                autocomplete="off"
                                @input="handleSearchInput"
                            />
                        </div>
                    </div>
                </div>

                <div class="table-responsive custom-scrollbar attendance-page__table-wrap mt-2">
                    <table class="table table-hover mb-0 attendance-page__table">
                        <thead>
                            <tr>
                                <th>
                                    <button type="button" class="attendance-page__sort-btn" @click="toggleSort('employee_name')">
                                        {{ $t('employee') }}
                                        <span>{{ sortIndicator('employee_name') }}</span>
                                    </button>
                                </th>
                                <th>
                                    <button type="button" class="attendance-page__sort-btn" @click="toggleSort('first_check_in')">
                                        {{ $t('clock_in') }}
                                        <span>{{ sortIndicator('first_check_in') }}</span>
                                    </button>
                                </th>
                                <th>
                                    <button type="button" class="attendance-page__sort-btn" @click="toggleSort('last_check_out')">
                                        {{ $t('clock_out') }}
                                        <span>{{ sortIndicator('last_check_out') }}</span>
                                    </button>
                                </th>
                                <th>
                                    <button type="button" class="attendance-page__sort-btn" @click="toggleSort('location_name')">
                                        {{ $t('location') }}
                                        <span>{{ sortIndicator('location_name') }}</span>
                                    </button>
                                </th>
                                <th>
                                    <button type="button" class="attendance-page__sort-btn" @click="toggleSort('shift_name')">
                                        {{ $t('shift') }}
                                        <span>{{ sortIndicator('shift_name') }}</span>
                                    </button>
                                </th>
                                <th>
                                    <button type="button" class="attendance-page__sort-btn" @click="toggleSort('total_hours')">
                                        {{ $t('duration') }}
                                        <span>{{ sortIndicator('total_hours') }}</span>
                                    </button>
                                </th>
                                <th>
                                    <button type="button" class="attendance-page__sort-btn" @click="toggleSort('status')">
                                        {{ $t('status') }}
                                        <span>{{ sortIndicator('status') }}</span>
                                    </button>
                                </th>
                                <th>{{ $t('device') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="row in rows"
                                :key="`${row.employee_id}-${row.date}`"
                                class="attendance-page__row"
                                :class="{ 'is-selected': isSelectedRow(row) }"
                                @click="openTimelineDrawer(row)"
                            >
                                <td>
                                    <div class="attendance-page__employee-cell">
                                        <div class="attendance-page__employee-avatar">
                                            <img
                                                :src="row.employee?.photo_url || getAppUrl('images/avatar-demo.jpg')"
                                                :alt="row.employee?.full_name || $t('employee')"
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </div>
                                        <div>
                                            <strong>{{ row.employee?.full_name || '-' }}</strong>
                                            <p class="mb-0 text-muted">{{ row.employee?.matricule || '-' }}</p>
                                        </div>
                                    </div>
                                </td>
                                <td>{{ formatTimeCell(row.first_check_in) }}</td>
                                <td>{{ formatTimeCell(row.last_check_out) }}</td>
                                <td>
                                    <div>
                                        <strong>{{ row.location?.name || '-' }}</strong>
                                        <p class="mb-0 text-muted">{{ row.location?.code || row.department?.name || '-' }}</p>
                                    </div>
                                </td>
                                <td>
                                    <div>
                                        <strong>{{ row.shift?.name || '-' }}</strong>
                                        <p class="mb-0 text-muted">{{ shiftScheduleLabel(row.shift) }}</p>
                                    </div>
                                </td>
                                <td>{{ formatDuration(row.total_hours) }}</td>
                                <td>
                                    <div class="attendance-page__status-stack">
                                        <span class="attendance-status-badge" :class="statusClass(row.status)">
                                            {{ statusLabel(row.status) }}
                                        </span>
                                        <span v-if="row.status_flags?.late" class="attendance-status-badge attendance-status-badge--late">{{ $t('late') }}</span>
                                        <span v-if="row.status_flags?.overtime" class="attendance-status-badge attendance-status-badge--overtime">{{ $t('overtime') }}</span>
                                        <span v-if="row.status_flags?.suspicious" class="attendance-status-badge attendance-status-badge--suspicious">{{ $t('suspicious') }}</span>
                                        <span v-if="row.day_context?.is_holiday" class="attendance-status-badge attendance-status-badge--holiday">{{ $t('holiday') }}</span>
                                        <span v-else-if="row.day_context?.is_day_off" class="attendance-status-badge attendance-status-badge--day-off">{{ $t('day_off') }}</span>
                                    </div>
                                </td>
                                <td>
                                    <div>
                                        <strong>{{ primaryDevice(row) }}</strong>
                                        <p class="mb-0 text-muted">{{ primaryMethod(row) }}</p>
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="!rows.length && !isLoading">
                                <td colspan="8">
                                    <div class="attendance-page__empty-state">
                                        <app-icon name="clock" />
                                        <p class="mb-0">{{ $t('attendance_empty_state') }}</p>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="attendance-page__pagination mt-4" v-if="pagination.total > 0">
                    <div>
                        <p class="text-muted mb-0">{{ paginationLabel }}</p>
                    </div>
                    <div class="attendance-page__pagination-controls">
                        <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="pagination.page <= 1 || isLoading" @click="goToPage(pagination.page - 1)">
                            {{ $t('previous') }}
                        </button>
                        <span class="attendance-page__pagination-state">
                            {{ pagination.page }} / {{ pagination.totalPages || 1 }}
                        </span>
                        <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="pagination.page >= pagination.totalPages || isLoading" @click="goToPage(pagination.page + 1)">
                            {{ $t('next') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <attendance-add-edit-modal
            v-if="isAddEditModalActive"
            :table-id="tableId"
            :selected-url="selectedUrl"
            @close-modal="closeAddEditModal"
        />

        <attendance-timeline-drawer
            v-if="selectedAttendance"
            :record="selectedAttendance"
            @close="closeTimelineDrawer"
        />
    </div>
</template>

<script>
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import AttendanceTimelineDrawer from './AttendanceTimelineDrawer.vue';
import { ATTENDANCE_TABLE } from '../../../Config/ApiUrl';

export default {
    name: 'AppAttendances',
    extends: CoreLibrary,
    components: {
        AttendanceTimelineDrawer,
    },
    data() {
        const today = window.moment ? window.moment().format('YYYY-MM-DD') : new Date().toISOString().split('T')[0];

        return {
            tableId: 'attendance-table-modern',
            isAddEditModalActive: false,
            selectedUrl: '',
            filtersOpen: false,
            isLoading: false,
            rows: [],
            meta: {
                total_present: 0,
                total_absent: 0,
                total_late: 0,
                total_overtime: 0,
                total_suspicious: 0,
            },
            pagination: {
                page: 1,
                perPage: 15,
                total: 0,
                totalPages: 1,
                count: 0,
            },
            sort: {
                field: 'date',
                direction: 'desc',
            },
            filters: {
                start_date: today,
                end_date: today,
                employee_id: '',
                location_id: '',
                shift_id: '',
                department_id: '',
                status: '',
            },
            searchQuery: '',
            searchTimer: null,
            selectedAttendance: null,
            perPageOptions: [10, 15, 25, 50],
            statusOptions: [
                { id: 'on_time', value: this.$t('on_time') },
                { id: 'late', value: this.$t('late') },
                { id: 'absent', value: this.$t('absent') },
                { id: 'overtime', value: this.$t('overtime') },
                { id: 'suspicious', value: this.$t('suspicious') },
            ],
        };
    },
    computed: {
        employeeOptions() {
            return this.$store.getters.getEmployeeList || [];
        },
        locationOptions() {
            return this.$store.getters.getLocationList || [];
        },
        departmentOptions() {
            return this.$store.getters.getDepartmentList || [];
        },
        shiftOptions() {
            return this.$store.getters.getWorkingShiftList || [];
        },
        activeFilterCount() {
            return [
                this.filters.employee_id,
                this.filters.location_id,
                this.filters.shift_id,
                this.filters.department_id,
                this.filters.status,
                this.searchQuery,
            ].filter((value) => !!value).length;
        },
        selectedDateLabel() {
            if (this.filters.start_date === this.filters.end_date) {
                return this.formatShortDate(this.filters.start_date);
            }

            return `${this.formatShortDate(this.filters.start_date)} - ${this.formatShortDate(this.filters.end_date)}`;
        },
        tableSummary() {
            return this.interpolateTemplate(
                this.$t('attendance_results_summary'),
                {
                    count: this.formatCountValue(this.pagination.total || 0),
                    date_range: this.selectedDateLabel,
                }
            );
        },
        paginationLabel() {
            if (!this.pagination.total) {
                return '';
            }

            const from = ((this.pagination.page - 1) * this.pagination.perPage) + 1;
            const to = Math.min(this.pagination.page * this.pagination.perPage, this.pagination.total);

            return this.$t('attendance_pagination_summary', {
                from,
                to,
                total: this.pagination.total,
            });
        },
    },
    mounted() {
        this.bootstrapPage();
    },
    beforeDestroy() {
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
    },
    methods: {
        async bootstrapPage() {
            await Promise.all([
                this.$store.dispatch('getEmployees'),
                this.$store.dispatch('getLocations'),
                this.$store.dispatch('getDepartments'),
                this.$store.dispatch('getWorkingShifts'),
            ]);

            this.loadAttendances();
        },
        openAddEditModal() {
            this.isAddEditModalActive = true;
        },
        closeAddEditModal() {
            this.isAddEditModalActive = false;
            this.selectedUrl = '';
            this.refreshListing();
        },
        toggleFilters() {
            this.filtersOpen = !this.filtersOpen;
        },
        refreshListing() {
            this.loadAttendances();
        },
        handleSearchInput() {
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }

            this.searchTimer = setTimeout(() => {
                this.pagination.page = 1;
                this.loadAttendances();
            }, 300);
        },
        applyFilters() {
            this.pagination.page = 1;
            this.loadAttendances();
        },
        clearFilters() {
            const today = window.moment ? window.moment().format('YYYY-MM-DD') : new Date().toISOString().split('T')[0];
            this.filters = {
                start_date: today,
                end_date: today,
                employee_id: '',
                location_id: '',
                shift_id: '',
                department_id: '',
                status: '',
            };
            this.searchQuery = '';
            this.pagination.page = 1;
            this.loadAttendances();
        },
        changePerPage() {
            this.pagination.page = 1;
            this.loadAttendances();
        },
        goToPage(page) {
            if (page < 1 || page > this.pagination.totalPages) {
                return;
            }

            this.pagination.page = page;
            this.loadAttendances();
        },
        toggleSort(field) {
            if (this.sort.field === field) {
                this.sort.direction = this.sort.direction === 'asc' ? 'desc' : 'asc';
            } else {
                this.sort.field = field;
                this.sort.direction = field === 'date' ? 'desc' : 'asc';
            }

            this.pagination.page = 1;
            this.loadAttendances();
        },
        interpolateTemplate(template, replacements = {}) {
            if (typeof template !== 'string') {
                return '';
            }

            return Object.keys(replacements).reduce((message, key) => {
                const pattern = new RegExp(`:${key}\\b`, 'g');
                return message.replace(pattern, replacements[key] ?? '');
            }, template);
        },
        sortIndicator(field) {
            if (this.sort.field !== field) {
                return '↕';
            }

            return this.sort.direction === 'asc' ? '↑' : '↓';
        },
        buildDateRangeParam() {
            let start = this.filters.start_date;
            let end = this.filters.end_date;

            if (!start && !end) {
                return '';
            }

            if (!start) {
                start = end;
            }

            if (!end) {
                end = start;
            }

            if (start > end) {
                return `${end},${start}`;
            }

            return `${start},${end}`;
        },
        async loadAttendances() {
            this.isLoading = true;

            try {
                const params = {
                    page: this.pagination.page,
                    per_page: this.pagination.perPage,
                    sort: `${this.sort.field}:${this.sort.direction}`,
                };

                const dateRange = this.buildDateRangeParam();
                if (dateRange) {
                    params.date_range = dateRange;
                }
                if (this.filters.employee_id) {
                    params.employee_id = this.filters.employee_id;
                }
                if (this.filters.location_id) {
                    params.location_id = this.filters.location_id;
                }
                if (this.filters.shift_id) {
                    params.shift_id = this.filters.shift_id;
                }
                if (this.filters.department_id) {
                    params.department_id = this.filters.department_id;
                }
                if (this.filters.status) {
                    params.status = this.filters.status;
                }
                if (this.searchQuery) {
                    params.search = this.searchQuery;
                }

                const response = await this.axiosGet(ATTENDANCE_TABLE, { params });
                const payload = response.data || {};
                const pagination = payload.meta?.pagination || {};

                this.rows = payload.data || [];
                this.meta = {
                    total_present: payload.meta?.total_present || 0,
                    total_absent: payload.meta?.total_absent || 0,
                    total_late: payload.meta?.total_late || 0,
                    total_overtime: payload.meta?.total_overtime || 0,
                    total_suspicious: payload.meta?.total_suspicious || 0,
                };
                this.pagination = {
                    page: pagination.current_page || 1,
                    perPage: pagination.per_page || this.pagination.perPage,
                    total: pagination.total || 0,
                    totalPages: pagination.total_pages || 1,
                    count: pagination.count || 0,
                };

                if (this.selectedAttendance) {
                    const stillSelected = this.rows.find((row) => this.isSameRow(row, this.selectedAttendance));
                    if (stillSelected) {
                        this.selectedAttendance = stillSelected;
                    }
                }
            } catch (error) {
                this.$toastr.e(this.$t('error_loading_attendance_listing'));
            } finally {
                this.isLoading = false;
            }
        },
        openTimelineDrawer(row) {
            this.selectedAttendance = row;
        },
        closeTimelineDrawer() {
            this.selectedAttendance = null;
        },
        isSelectedRow(row) {
            return this.selectedAttendance && this.isSameRow(row, this.selectedAttendance);
        },
        isSameRow(first, second) {
            return `${first.employee_id}-${first.date}` === `${second.employee_id}-${second.date}`;
        },
        formatShortDate(value) {
            if (!value) {
                return '-';
            }

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return value;
            }

            return new Intl.DateTimeFormat(undefined, {
                month: 'short',
                day: 'numeric',
            }).format(date);
        },
        formatTimeCell(value) {
            if (!value) {
                return '-';
            }

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return value;
            }

            return new Intl.DateTimeFormat(undefined, {
                hour: '2-digit',
                minute: '2-digit',
            }).format(date);
        },
        formatDuration(value) {
            const totalHours = Number(value || 0);
            if (!Number.isFinite(totalHours) || totalHours <= 0) {
                return '-';
            }

            const totalMinutes = Math.round(totalHours * 60);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;

            return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
        },
        shiftScheduleLabel(shift) {
            if (!shift || !shift.start_at || !shift.end_at) {
                return this.$t('not_available');
            }

            return `${shift.start_at} - ${shift.end_at}`;
        },
        primaryDevice(row) {
            return row.device_info?.devices?.[0] || '-';
        },
        primaryMethod(row) {
            return row.device_info?.methods?.[0] || this.$t('not_available');
        },
        statusLabel(status) {
            return this.$t(status || 'untracked');
        },
        statusClass(status) {
            const classes = {
                on_time: 'attendance-status-badge--ontime',
                present: 'attendance-status-badge--present',
                late: 'attendance-status-badge--late',
                absent: 'attendance-status-badge--absent',
                overtime: 'attendance-status-badge--overtime',
                suspicious: 'attendance-status-badge--suspicious',
                untracked: 'attendance-status-badge--neutral',
            };

            return classes[status] || classes.untracked;
        },
    },
};
</script>

<style scoped>
.attendance-page__intro {
    max-width: 44rem;
}

.attendance-page__top-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.attendance-page__view-switch .btn {
    min-width: 7.5rem;
}

.attendance-page__filter-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.4rem;
    height: 1.4rem;
    margin-left: 0.45rem;
    border-radius: 999px;
    background: rgba(37, 99, 235, 0.12);
    color: #1d4ed8;
    font-size: 0.72rem;
    font-weight: 700;
}

.attendance-page__stat-card {
    height: 100%;
    padding: 1rem 1.1rem;
    border-radius: 1rem;
    background: #fff;
    border: 1px solid rgba(148, 163, 184, 0.16);
}

.attendance-page__stat-card span {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #64748b;
}

.attendance-page__stat-card strong {
    font-size: 1.45rem;
    line-height: 1;
    color: #0f172a;
}

.attendance-page__stat-card--present {
    border-color: rgba(16, 185, 129, 0.18);
}

.attendance-page__stat-card--absent {
    border-color: rgba(239, 68, 68, 0.18);
}

.attendance-page__stat-card--late {
    border-color: rgba(245, 158, 11, 0.2);
}

.attendance-page__stat-card--overtime {
    border-color: rgba(59, 130, 246, 0.18);
}

.attendance-page__stat-card--suspicious {
    border-color: rgba(168, 85, 247, 0.18);
}

.attendance-page__stat-card--range {
    border-color: rgba(71, 85, 105, 0.18);
}

.attendance-page__filters-panel,
.attendance-page__table-shell {
    padding: 1.5rem;
}

.attendance-page__filters-head,
.attendance-page__table-toolbar {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
}

.attendance-page__label {
    display: inline-block;
    margin-bottom: 0.45rem;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #64748b;
}

.attendance-page__table-toolbar-actions {
    min-width: min(100%, 22rem);
}

.attendance-page__search-wrap {
    display: flex;
    align-items: center;
    min-width: min(100%, 22rem);
    padding: 0 1rem;
    border: 1px solid #d9e2ec;
    border-radius: 999px;
    background: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.attendance-page__search-wrap:focus-within {
    border-color: #2563eb;
}

.attendance-page__search-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 1rem;
    width: 1rem;
    min-width: 1rem;
    margin-right: 0.75rem;
    color: #94a3b8;
}

.attendance-page__search-input {
    flex: 1 1 auto;
    min-width: 0;
    border: 0 !important;
    border-radius: 0 !important;
    padding: 0.85rem 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
}

.attendance-page__search-input:focus {
    box-shadow: none !important;
}

.attendance-page__search-input::placeholder {
    color: #94a3b8;
}

.attendance-page__search-icon {
    width: 1rem;
    height: 1rem;
    pointer-events: none;
}

.attendance-page__table-wrap {
    border: 1px solid rgba(148, 163, 184, 0.16);
    border-radius: 1rem;
    overflow: hidden;
    background: #fff;
}

.attendance-page__table {
    min-width: 1100px;
}

.attendance-page__table thead th {
    border-top: 0;
    background: #f8fafc;
    color: #475569;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    white-space: nowrap;
}

.attendance-page__sort-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
}

.attendance-page__row {
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.2s ease;
}

.attendance-page__row:hover {
    background: rgba(37, 99, 235, 0.035);
}

.attendance-page__row.is-selected {
    background: rgba(37, 99, 235, 0.08);
}

.attendance-page__employee-cell {
    display: flex;
    align-items: center;
    gap: 0.85rem;
}

.attendance-page__employee-avatar {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 999px;
    overflow: hidden;
    background: #e2e8f0;
    flex-shrink: 0;
}

.attendance-page__employee-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.attendance-page__status-stack {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: wrap;
}

.attendance-status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    padding: 0.45rem 0.8rem;
    font-size: 0.74rem;
    font-weight: 700;
    line-height: 1;
}

.attendance-status-badge--ontime,
.attendance-status-badge--present {
    background: rgba(16, 185, 129, 0.14);
    color: #047857;
}

.attendance-status-badge--late {
    background: rgba(245, 158, 11, 0.16);
    color: #b45309;
}

.attendance-status-badge--absent {
    background: rgba(239, 68, 68, 0.14);
    color: #b91c1c;
}

.attendance-status-badge--overtime {
    background: rgba(59, 130, 246, 0.14);
    color: #1d4ed8;
}

.attendance-status-badge--suspicious {
    background: rgba(168, 85, 247, 0.15);
    color: #7e22ce;
}

.attendance-status-badge--holiday {
    background: rgba(14, 165, 233, 0.14);
    color: #0369a1;
}

.attendance-status-badge--day-off,
.attendance-status-badge--neutral {
    background: rgba(100, 116, 139, 0.14);
    color: #475569;
}

.attendance-page__empty-state {
    min-height: 18rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.8rem;
    color: #64748b;
    text-align: center;
}

.attendance-page__pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
}

.attendance-page__pagination-controls {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.attendance-page__pagination-state {
    min-width: 4.5rem;
    text-align: center;
    font-weight: 700;
    color: #334155;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

@media (max-width: 991.98px) {
    .attendance-page__filters-panel,
    .attendance-page__table-shell {
        padding: 1rem;
    }
}
</style>
